import type { OverlayEvent } from '@shared/types'

import { getEffectDuration, isExclusiveEvent, isQueueBypassEvent } from '@entities/overlay-event'
import { OVERLAY_CONFIG } from '@shared/config'
import { create } from 'zustand'

export interface ActiveEffect {
  id: string
  event: OverlayEvent
  startedAt: number
  duration: number
  /**
   * Событие активировано в обход лимита очереди (зональные виджеты
   * poll/drawing/soundboard и долгоживущие алерты donation_alert/text_alert) —
   * не участвует в подсчёте QUEUE_MAX_CONCURRENT и не блокируется/не блокирует
   * exclusiveMode. Кэшируется при активации, чтобы не пересчитывать по isQueueBypassEvent
   * при каждой реконцилиации.
   */
  bypass: boolean
}

/** Pending-слот с отметкой времени постановки в очередь (для TTL) */
interface PendingEntry {
  event: OverlayEvent
  enqueuedAt: number
}

interface EffectQueueState {
  active: ActiveEffect[]
  pending: PendingEntry[]
  exclusiveMode: boolean

  enqueue: (event: OverlayEvent) => void
  complete: (id: string) => void
}

// ─── Утилиты ──────────────────────────────────────────────────────────────────

/**
 * Фильтрует pending-записи, удаляя устаревшие по TTL.
 * DRY: единая точка TTL-фильтрации — вызывается при enqueue и complete.
 *
 * Узкое место / рекомендация:
 *   TTL проверяется лениво (при добавлении или завершении эффекта).
 *   Если очередь накопилась, а новые события не приходят — устаревшие
 *   записи будут лежать до следующего вызова enqueue/complete.
 *   Для стримов с редкими событиями можно добавить фоновый setInterval,
 *   вызывающий purgePending() раз в N секунд.
 */
function purgePending(entries: PendingEntry[], now: number): PendingEntry[] {
  const ttl = OVERLAY_CONFIG.QUEUE_PENDING_TTL_MS
  if (ttl <= 0) return entries   // TTL отключён
  const purged = entries.filter((e) => now - e.enqueuedAt < ttl)
  const dropped = entries.length - purged.length
  if (dropped > 0) {
    // Очистка по TTL: просроченные события отбрасываются молча
  }
  return purged
}

function makeActive(event: OverlayEvent): ActiveEffect {
  return {
    id: event.id,
    event,
    startedAt: Date.now(),
    duration: getEffectDuration(event),
    bypass: isQueueBypassEvent(event),
  }
}

// ─── Хранилище ──────────────────────────────────────────────────────────────

export const useEffectQueue = create<EffectQueueState>((set, get) => ({
  active: [],
  pending: [],
  exclusiveMode: false,

  /**
   * Добавляет событие в очередь или активирует сразу.
   *
   * Логика:
   *  1. ping игнорируется
   *  2. TTL-очистка pending перед принятием решения
   *  3. Дедуп по id: если событие с тем же id уже активно или ожидает —
   *     обновляем его event «на месте» (см. ниже), не создавая дубликат
   *  4. Bypass-события (зональные poll/drawing/soundboard, долгоживущие
   *     donation_alert/text_alert — см. isQueueBypassEvent) активируются
   *     СРАЗУ, без учёта QUEUE_MAX_CONCURRENT и exclusiveMode — у них своя
   *     зона/слот, они физически не конкурируют за экран с остальными и не
   *     должны ждать освобождения слотов custom_effects/коротких эффектов.
   *  5. Остальные (короткие наслаивающиеся: fireworks, custom_effects) — как
   *     раньше: если свободны слоты среди НЕ-bypass активных и нет
   *     exclusiveMode → активируем сразу, иначе → pending.
   */
  enqueue: (event: OverlayEvent) => {
    if (event.type === 'ping') return

    const now = Date.now()
    const { active, pending, exclusiveMode } = get()
    const max = OVERLAY_CONFIG.QUEUE_MAX_CONCURRENT

    // TTL-очистка перед обработкой нового события
    const cleanPending = purgePending(pending, now)

    // ── Дедуп по стабильному id (например, poll_${sessionId}) ──────────────
    // Бэкенд переиспользует один overlay-id для обновлений одного и того же
    // виджета (голосование/смена голоса). Если такой id уже в active —
    // подменяем только event, СОХРАНЯЯ id/startedAt/duration. React по
    // key={id} переиспользует тот же инстанс <PollOverlay> с новыми props —
    // хуки живы, без мерцания и без дубля.
    if (active.some((e) => e.id === event.id)) {
      set({
        active: active.map((e) => (e.id === event.id ? { ...e, event } : e)),
        pending: cleanPending,
      })
      return
    }

    // Тот же id ещё ждёт в pending — обновляем event на месте.
    if (cleanPending.some((e) => e.event.id === event.id)) {
      set({
        pending: cleanPending.map((e) =>
          e.event.id === event.id ? { ...e, event } : e,
        ),
      })
      return
    }

    // Bypass-событие (зона/долгоживущий алерт) — активируем сразу, минуя
    // лимит слотов и exclusiveMode целиком. Не блокируется активным
    // custom_effects и сам не занимает общий слот.
    if (isQueueBypassEvent(event)) {
      set({ active: [...active, makeActive(event)], pending: cleanPending })
      return
    }

    // Далее рассуждаем только про НЕ-bypass активные эффекты — зональные/
    // долгоживущие уже активны, но не занимают общий лимит и не участвуют в
    // exclusiveMode-блокировке (только обычные наслаивающиеся эффекты).
    const nonBypassActive = active.filter((e) => !e.bypass)

    // Гонка эксклюзивности (см. отчёт аудита §3.2): раньше проверялся только
    // ТЕКУЩИЙ exclusiveMode, но не эксклюзивность ВХОДЯЩЕГО события. Из-за
    // этого при active.length < max эксклюзивное событие (custom_effects)
    // могло активироваться сразу и отрендериться ОДНОВРЕМЕННО с уже играющим
    // неэксклюзивным эффектом. Правильное правило: эксклюзивное событие может
    // начать играть только когда очередь НЕ-bypass активных эффектов
    // полностью пуста — если nonBypassActive не пуст (независимо от типа
    // того, что там играет), новое эксклюзивное событие всегда уходит в
    // pending и ждёт освобождения слотов. Bypass-события (зональные/
    // долгоживущие) в этот подсчёт не входят и не мешают custom_effects.
    const incomingExclusive = isExclusiveEvent(event)
    const shouldQueue =
      exclusiveMode ||
      nonBypassActive.length >= max ||
      (incomingExclusive && nonBypassActive.length > 0)

    if (!shouldQueue) {
      set({
        active: [...active, makeActive(event)],
        pending: cleanPending,
        exclusiveMode: incomingExclusive,
      })
    } else {
      set({
        pending: [...cleanPending, { event, enqueuedAt: now }],
      })
    }
  },

  /**
   * Завершает активный эффект и активирует следующий из pending.
   *
   * Логика:
   *  1. Убираем завершённый эффект из active
   *  2. Bypass-эффекты (зональные/долгоживущие) НЕ входят в подсчёт «остались
   *     ли активные» для exclusiveMode/лимита слотов — они всегда активны
   *     параллельно и не должны блокировать выход из exclusiveMode или
   *     подхват pending-эффектов. Считаем отдельно nonBypassRemaining.
   *  3. Если exclusiveMode и nonBypassRemaining ещё не пуст → просто ждём
   *  4. TTL-очистка pending
   *  5. Достаём из pending столько событий, сколько влезает в НЕ-bypass слоты
   *  6. Если среди новых активированных есть priority → включаем exclusiveMode
   *
   * ПРИМЕЧАНИЕ: pending всегда содержит только НЕ-bypass события — bypass-
   * события активируются мгновенно в enqueue() и никогда не встают в pending.
   */
  complete: (id: string) => {
    const now = Date.now()
    const { active, pending, exclusiveMode } = get()
    const remaining = active.filter((e) => e.id !== id)
    const nonBypassRemaining = remaining.filter((e) => !e.bypass)
    const max = OVERLAY_CONFIG.QUEUE_MAX_CONCURRENT

    if (exclusiveMode && nonBypassRemaining.length > 0) {
      set({ active: remaining })
      return
    }

    // TTL-очистка при освобождении слота
    const cleanPending = purgePending(pending, now)

    // Та же гарантия эксклюзивности, что и в enqueue(): если следующий по
    // очереди pending-эффект эксклюзивен, активируем ТОЛЬКО его одного (и
    // только когда nonBypassRemaining пуст), не подхватывая рядом другие
    // pending-события в свободные слоты. Обычные (неэксклюзивные) эффекты
    // продолжают заполнять все свободные НЕ-bypass слоты как раньше.
    // Bypass-эффекты в remaining (зональные/долгоживущие) не расходуют слоты.
    const freeSlots = max - nonBypassRemaining.length
    let toActivate: OverlayEvent[] = []
    let stillPending = cleanPending

    if (freeSlots > 0 && cleanPending.length > 0) {
      const head = cleanPending[0].event
      if (isExclusiveEvent(head)) {
        if (nonBypassRemaining.length === 0) {
          toActivate = [head]
          stillPending = cleanPending.slice(1)
        }
        // nonBypassRemaining.length > 0 → эксклюзивный head продолжает ждать в pending.
      } else {
        // Берём подряд идущие неэксклюзивные события, останавливаясь перед
        // первым эксклюзивным — оно не должно активироваться вместе с другими.
        let i = 0
        while (i < freeSlots && i < cleanPending.length && !isExclusiveEvent(cleanPending[i].event)) {
          i++
        }
        toActivate = cleanPending.slice(0, i).map((e) => e.event)
        stillPending = cleanPending.slice(i)
      }
    }

    const nextExclusive = toActivate.some(isExclusiveEvent)

    set({
      active: [...remaining, ...toActivate.map(makeActive)],
      pending: stillPending,
      exclusiveMode: nextExclusive,
    })
  },
}))
